/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */

import type { PricingPlan, PricingFAQ } from "./types";
import { AI_TOOL_COUNT } from "./constants";

/**
 * Pricing Plans (tier matrix v3 — 2026-04-13)
 *
 * 4 paid tiers: Mobile ($29) → Starter ($79) → Professional ($149) → Business ($299).
 * Free Trial is mobile-app-only (5 lifetime Quick Estimates) — see /download.
 */

export const pricingPlans: PricingPlan[] = [
  {
    name: "Mobile",
    price: "$29",
    period: "/user/month",
    annualPrice: "$24",
    description:
      "For contractors on the go. Walk the site, snap photos, and send a proposal before you leave the driveway.",
    features: [
      "Mobile app only — no web access",
      "25 AI Quick Estimates/month",
      "$9/mo AI budget",
      "AI-scaffolded estimates (save + send)",
      "Basic proposal templates",
      "Time entries on your own jobs",
      "Client portal sharing",
      "5 GB storage",
    ],
    popular: false,
    planId: "mobile_plan",
  },
  {
    name: "Starter",
    price: "$79",
    period: "/user/month",
    annualPrice: "$66",
    description:
      "Full web platform for solo contractors. Labor rate + burden + AI search — no cost database needed.",
    features: [
      "Mobile app included",
      "Full web platform access",
      "50 AI Quick Estimates/month",
      "$32/mo AI budget",
      "Labor rate + burden · AI pricing search",
      "Unlimited AI-scaffolded estimates",
      "3 proposal templates",
      "Change orders — submit + approve",
      "Time entries",
      "Client portal · QuickBooks import",
      "1 office seat",
      "10 GB storage",
    ],
    popular: true,
    planId: "starter_plan",
  },
  {
    name: "Professional",
    price: "$149",
    period: "/user/month",
    annualPrice: "$124",
    description:
      "For teams that bid together. Own your cost database, your templates, and your margins.",
    features: [
      "Mobile app included",
      "Everything in Starter",
      "250 AI Quick Estimates/month",
      "$60/mo AI budget",
      "Pre-loaded cost DB (materials, equipment, labor — 8 trades)",
      "Variance factors + assemblies + templates",
      "Blueprint takeoffs",
      `Full AI assistant (${AI_TOOL_COUNT} tools)`,
      "Change orders · POs · bids · subs (full lifecycle)",
      "Offline mode (field-ready)",
      "5 office seats + 10 field seats",
      "50 GB storage",
    ],
    popular: false,
    planId: "professional_plan",
  },
  {
    name: "Business",
    price: "$299",
    period: "/user/month",
    annualPrice: "$249",
    description:
      "For multi-division operations. Every feature, multi-division rollups, API access, priority support.",
    features: [
      "Mobile app included",
      "Everything in Professional",
      "Unlimited AI Quick Estimates (fair use)",
      "$120/mo AI budget",
      "Multi-division cost DB + rollups",
      "E-sign + branded client portal",
      "AI blueprint measurement assist",
      "API access for integrations",
      "Advanced permissions & roles",
      "15 office seats + 30 field seats",
      "Offline mode — priority support",
      "100 GB storage",
    ],
    popular: false,
    planId: "business_plan",
  },
];

/**
 * Pricing FAQ (Quick Questions)
 * Displayed in the pricing section
 */

export const pricingFAQ: PricingFAQ[] = [
  {
    question: "Is there a free trial?",
    answer:
      "Download the mobile app for 5 free AI Quick Estimates — no signup, no card. To save estimates, send proposals, or access the web platform, pick any paid plan. Cancel anytime.",
  },
  {
    question: "Do I need to build my own cost database on Starter?",
    answer:
      "No. Starter prices jobs from your labor rate + burden plus AI pricing search. Upgrade to Professional when you want to own your cost data with a pre-loaded database for materials, equipment, and labor.",
  },
  {
    question: "What happens if I hit my AI Quick Estimate limit?",
    answer:
      "Starter: we'll prompt you to upgrade. Professional: soft cap with a short pause before it resets. Business: unlimited under fair use.",
  },
  {
    question: "Can I switch plans?",
    answer:
      "Yes — upgrade or downgrade anytime. Changes take effect immediately and are prorated.",
  },
  {
    question: "Is pricing per user or per company?",
    answer:
      "Per user, per month. Mobile and Starter are solo plans. Professional and Business include office seats + field seats for your crew.",
  },
  {
    question: "Do you offer annual billing?",
    answer: "Yes. Annual plans save ~15% — billed yearly instead of monthly.",
  },
];

/**
 * Pricing Page Content
 */

export const pricingPageContent = {
  hero: {
    title: "Plans that pay for themselves with one won bid",
    description:
      "Pick the plan that fits how you work. Upgrade the day your team grows. Cancel anytime.",
    roiHook: null,
  },
  faq: {
    title: "Questions?",
  },
  footer: {
    text: "For a complete list of capabilities, see",
    linkText: "Features",
    linkHref: "/features",
  },
};
